import ControlBase from '../fabric/ControlBase';
import { type fabric } from 'fabric';
import type CanvaSXBehaviors from '../objects/CanvaSXBehaviors';
import { type IDataStoreRecordBehaviors } from '../collaboration/DataStoreRecordBehaviors';
import { OPEN_RECORD, renderIcon } from '../objects/IconsHelper';
import { isShape } from '../objects/guards';

type WhiteboardObject = fabric.Object &
  CanvaSXBehaviors &
  IDataStoreRecordBehaviors;

const RADIUS = 13;
const PADDING = 6;
const HIDE_AT_ZOOM = 0.7;

class OpenAhaRecordLinkControl extends ControlBase {
  actionName = 'vote';
  minDimension = 0; // Disable control hiding
  sizeX = 0;
  sizeY = 0;
  left = 0;
  top = 0;
  cursorStyle = 'pointer';
  hoveredControlObject?: WhiteboardObject;

  getVisibility(fabricObject: WhiteboardObject, key: string): boolean {
    if (
      // Don't show the icon for record cards
      (isShape(fabricObject) && fabricObject.shapeName === 'record') ||
      // Only show it if we have a record link on the shape
      !(Boolean(fabricObject.recordType) && Boolean(fabricObject.recordId)) ||
      // When they select the shape we want to hide the link to give more
      // visibility to the content underneath
      fabricObject.isSelected() ||
      window.isPublishing ||
      fabricObject.controller.canvas?.exporting
    ) {
      return false;
    }

    const leftOffset = this.leftOffset(fabricObject);

    const zoom = fabricObject.canvas.getZoom();
    const minHeight = (RADIUS * 2 + PADDING) / fabricObject.scaleY / zoom;

    // When there's a link, we need to check if there's enough width for both controls
    const minWidth =
      leftOffset > 0
        ? (2 * RADIUS * 2 + 2 * PADDING) / fabricObject.scaleX / zoom
        : 0;

    // Hide the control if the object is too small or zoom is too low
    if (
      zoom <= HIDE_AT_ZOOM ||
      fabricObject.height < minHeight ||
      (leftOffset > 0 && fabricObject.width < minWidth)
    ) {
      return false;
    }

    return super.getVisibility(fabricObject, key);
  }

  getMouseUpHandler(
    eventData: MouseEvent,
    fabricObject: fabric.Object
  ): fabric.ControlMouseEventHandler {
    return (e: MouseEvent) => {
      const ref = fabricObject.recordReference();
      if (ref) {
        fabricObject.controller.openRecordInDrawer(ref, e);
      }
      return false;
    };
  }

  containsPoint(
    point: { x: number; y: number },
    fabricObject: WhiteboardObject
  ): boolean {
    const zoom = fabricObject.canvas.getZoom();

    const relativePoint = fabricObject.canvas.pointToCanvasCoords(point);

    const iconx =
      fabricObject.left +
      fabricObject.width * fabricObject.scaleX -
      (RADIUS + PADDING) / zoom -
      this.leftOffset(fabricObject) / zoom;

    const icony = fabricObject.top + (RADIUS + PADDING) / zoom;

    return (
      Math.hypot(relativePoint.x - iconx, relativePoint.y - icony) <
      RADIUS / zoom
    );
  }

  onMouseOver(eventData: MouseEvent, fabricObject: WhiteboardObject): void {
    this.hoveredControlObject = fabricObject;
  }

  onMouseOut(): void {
    this.hoveredControlObject = undefined;
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: any,
    fabricObject: WhiteboardObject
  ): void {
    const zoom = fabricObject.canvas.getZoom();

    const alpha =
      this.hoveredControlObject === fabricObject
        ? 1
        : fabricObject.isHovered()
        ? 0.7
        : 0.4;
    const fillStyle = `rgba(51, 51, 51, ${alpha})`;

    ctx.save();
    ctx.beginPath();
    ctx.translate(left, top);
    ctx.translate(
      (fabricObject.width / 2) * fabricObject.scaleX * zoom -
        (RADIUS + PADDING) -
        this.leftOffset(fabricObject),
      (-fabricObject.height / 2) * fabricObject.scaleY * zoom +
        (RADIUS + PADDING)
    );
    ctx.arc(0, 0, RADIUS, 0, 2 * Math.PI);
    ctx.fillStyle = fillStyle;
    ctx.fill();
    ctx.translate(-5.5, -5);
    ctx.scale(0.35, 0.35);
    renderIcon({
      ctx,
      icon: OPEN_RECORD,
      fillStyle: 'rgba(255, 255, 255)',
    });
    ctx.restore();
  }

  // If the object has a link on it, then shift this over to the left that
  private leftOffset(fabricObject: WhiteboardObject): number {
    return fabricObject.linkToUrl ? 2 * RADIUS + PADDING : 0;
  }
}

export default OpenAhaRecordLinkControl;
